import React from "react";
import {
  Compass,
  Sparkles,
  Home,
  ShieldCheck,
  FileText,
  Users,
  MapPin,
  BadgeCheck,
} from "lucide-react";
import Reveal from "../Reveal";

const STEPS = [
  {
    n: "01",
    title: "Browse & Shortlist",
    desc: "Explore our curated collection of marble lots. Save your favourites to your wishlist.",
    Icon: Compass,
  },
  {
    n: "02",
    title: "Request Free Samples",
    desc: "Tell us which lots you love. We'll prepare your samples - at no cost.",
    Icon: Sparkles,
  },
  {
    n: "03",
    title: "Expert Visits You",
    desc: "An Aurevia specialist arrives at your home with physical samples and verified slab images.",
    Icon: Home,
  },
  {
    n: "04",
    title: "Buy with Confidence",
    desc: "Verify every slab before you commit. Place your order, on your terms.",
    Icon: ShieldCheck,
  },
];

const KISHANGARH_STEPS = [
  {
    n: "01",
    title: "Share Your Brief",
    desc: "Tell us your marble requirements, budget, and timeline. We plan everything around your needs.",
    Icon: FileText,
  },
  {
    n: "02",
    title: "Curated Vendors",
    desc: "We identify the right wholesale vendors and handpick lots that match your brief.",
    Icon: Users,
  },
  {
    n: "03",
    title: "Visit the Market",
    desc: "We accompanies you through the yards and perform thorough quality checks.",
    Icon: MapPin,
  },
  {
    n: "04",
    title: "Buy with Confidence",
    desc: "Verify every slab before you commit. Place your order, on your terms.",
    Icon: BadgeCheck,
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="section-how-it-works"
      className="relative py-16 md:py-24"
    >
      <div className="editorial-container">
        {/* Heading */}
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4 mb-7">
            <span className="h-px w-10 bg-brand-gold/70" />
            <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold">
              How It Works
            </p>
          </div>
          <h2
            data-testid="how-it-works-heading"
            className="font-serif font-light text-balance text-brand-cream leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(2rem, 4.4vw, 4rem)" }}
          >
            From Browse to Doorstep.{" "}
            <span className="italic text-brand-gold/90">In Four Steps.</span>
          </h2>
        </Reveal>

        {/* Sub-section 1: Buy From Home */}
        <Reveal className="mt-12 md:mt-16">
          <h3
            data-testid="subsection-buy-from-home-heading"
            className="font-serif font-light text-brand-cream text-2xl md:text-[28px]"
          >
            Buy From Home
          </h3>
        </Reveal>

        {/* Cards */}
        <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06]">
          {STEPS.map((s, i) => {
            const Icon = s.Icon;
            return (
              <Reveal
                key={s.n}
                delay={i * 0.08}
                className="group relative bg-brand-charcoal p-8 md:p-10 lg:p-12 transition-colors duration-500 hover:bg-[#1f1f1f]"
                data-testid={`how-step-${s.n}`}
              >
                {/* gold corner accent on hover */}
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-px w-0 bg-brand-gold transition-all duration-700 group-hover:w-12"
                />
                <span
                  aria-hidden
                  className="absolute top-0 left-0 w-px h-0 bg-brand-gold transition-all duration-700 group-hover:h-12"
                />

                <div className="flex items-start justify-between mb-12">
                  <Icon
                    className="h-7 w-7 text-brand-gold transition-transform duration-500 group-hover:-translate-y-0.5"
                    strokeWidth={1.25}
                  />
                  <span className="font-sans text-[11px] tracking-[0.3em] text-brand-gold/80">
                    {s.n}
                  </span>
                </div>

                <h3 className="font-serif text-2xl md:text-[26px] font-light text-brand-cream leading-snug">
                  {s.title}
                </h3>
                <p className="mt-4 font-sans text-sm text-brand-cream/60 leading-relaxed">
                  {s.desc}
                </p>
              </Reveal>
            );
          })}
        </div>

        {/* Hairline divider between the two How It Works sub-sections */}
        <Reveal className="mt-4 md:mt-6 mb-4 md:mb-6">
          <span className="block h-px w-full bg-brand-gold/25" />
        </Reveal>

        {/* Sub-section 2: Visit Kishangarh With Us */}
        <Reveal>
          <h3
            data-testid="subsection-visit-kishangarh-heading"
            className="font-serif font-light text-brand-cream text-2xl md:text-[28px]"
          >
            Visit Kishangarh With Us
          </h3>
        </Reveal>

        {/* Cards */}
        <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06]">
          {KISHANGARH_STEPS.map((s, i) => {
            const Icon = s.Icon;
            return (
              <Reveal
                key={s.n}
                delay={i * 0.08}
                className="group relative bg-brand-charcoal p-8 md:p-10 lg:p-12 transition-colors duration-500 hover:bg-[#1f1f1f]"
                data-testid={`kishangarh-step-${s.n}`}
              >
                {/* gold corner accent on hover */}
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-px w-0 bg-brand-gold transition-all duration-700 group-hover:w-12"
                />
                <span
                  aria-hidden
                  className="absolute top-0 left-0 w-px h-0 bg-brand-gold transition-all duration-700 group-hover:h-12"
                />

                <div className="flex items-start justify-between mb-12">
                  <Icon
                    className="h-7 w-7 text-brand-gold transition-transform duration-500 group-hover:-translate-y-0.5"
                    strokeWidth={1.25}
                  />
                  <span className="font-sans text-[11px] tracking-[0.3em] text-brand-gold/80">
                    {s.n}
                  </span>
                </div>

                <h3 className="font-serif text-2xl md:text-[26px] font-light text-brand-cream leading-snug">
                  {s.title}
                </h3>
                <p className="mt-4 font-sans text-sm text-brand-cream/60 leading-relaxed">
                  {s.desc}
                </p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
